<template>
  <v-col cols="12" md="6" sm="12">
    <v-card
      elevation="4"
      :class="
        trialExpired ? 'card-plan-expired' : isTrial ? 'card-plan-expires' : ''
      "
    >
      <v-card-title class="h6 pb-6 font-weight-bold">
        {{ $t("heading.instance.advanced.siteDetails.card.title") }}
      </v-card-title>
      <v-card-text class="pt-2">
        <template v-if="!loading">
          <div class="details-box">
            <div class="p-3 details">
              <span class="details-label">{{ $t("general.plan") }}</span>
              <div class="d-flex align-center justify-space-between">
                <div
                  class="details-value font-weight-bold"
                  :class="
                    trialExpired ? 'red--text' : isTrial ? 'orange--text' : ''
                  "
                >
                  <span>{{ instance.plan_name }}</span>
                  <span v-if="trialExpiresText" class="font-weight-400">
                    ({{ trialExpiresText }})</span
                  ><br />
                </div>
                <div class="details-action">
                  <v-btn
                    v-if="isTrial && trialUpgradeUrl"
                    x-small
                    :href="trialUpgradeUrl"
                    target="_blank"
                    :color="trialExpired ? 'red' : isTrial ? 'orange' : ''"
                    elevation="0"
                    class="white--text"
                  >
                    <span class="font-weight-bold">{{
                      $t("button.upgrade")
                    }}</span>
                    <v-icon class="ml-2 mr-0" size="20">$upgradeIcon</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div class="p-3 details">
              <span class="details-label">{{
                $t("heading.instance.advanced.siteDetails.card.site.domain")
              }}</span>
              <div>
                <span class="details-value heading--text font-weight-bold">
                  {{ instance.domain }}
                </span>
              </div>
            </div>
            <div class="p-3 details">
              <span class="details-label">{{
                $t("heading.instance.advanced.siteDetails.card.site.url")
              }}</span>
              <div>
                <a
                  class="details-value info--text font-weight-bold"
                  :href="instance.url"
                  target="_blank"
                >
                  {{ instance.url }}
                </a>
              </div>
            </div>
            <div
              v-if="instance.status?.wordpress !== null"
              class="p-3 details"
            >
              <span class="details-label">{{
                $t("heading.instance.advanced.siteDetails.card.site.version")
              }}</span>
              <div>
                <span class="details-value heading--text font-weight-bold">{{
                  instance.status.wordpress
                }}</span>
              </div>
            </div>
            <div class="p-3 details">
              <span class="details-label">{{
                $t("heading.instance.advanced.siteDetails.card.site.siteName")
              }}</span>
              <div>
                <span class="details-value heading--text font-weight-bold">
                  <editable-header
                    ref="siteNameHeader"
                    v-on="$listeners"
                    :inputRules="siteNameRules"
                    :inputValue="instance.title"
                    :loading="nameChangeLoad"
                    :instance="instance"
                  />
                </span>
              </div>
            </div>
            <div class="p-3 details">
              <span class="details-label">{{
                $t("heading.instance.advanced.siteDetails.card.site.created")
              }}</span>
              <div>
                <span class="details-value heading--text font-weight-bold">{{
                  instance.created_at_from_now
                }}</span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <v-skeleton-loader type="text@2" />

          <v-skeleton-loader type="text@2" />

          <v-skeleton-loader type="text@2" />

          <v-skeleton-loader type="text@2" />

          <v-skeleton-loader type="text@2" />
        </template>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import EditableHeader from "../../inputs/EditableHeader.vue";
import FormRulesMixin from "../../../mixins/FormRulesMixin";
import moment from "moment";

export default {
  components: {
    EditableHeader,
  },
  mixins: [FormRulesMixin],
  computed: {
    nameChangeLoad: function () {
      return this.$store.state.InstanceModule.nameChangingActionInProgress;
    },
    isTrial() {
      return this.instance.service_is_trial;
    },
    trialExpiryAt() {
      return this.instance.service_trial_expiry_at;
    },
    trialExpired() {
      if (!this.isTrial) {
        return false;
      }
      return moment(this.trialExpiryAt).isBefore(moment());
    },
    trialExpiresText() {
      if (!this.isTrial) {
        return "";
      }
      if (this.trialExpired) {
        return (
          this.$t("general.trialExpired") +
          " " +
          moment(this.trialExpiryAt).fromNow()
        );
      }
      return (
        this.$t("general.trialExpires") +
        " " +
        moment(this.trialExpiryAt).fromNow()
      );
    },
    trialUpgradeUrl() {
      if (this.instance.plan_upgrade_link) {
        return this.instance.plan_upgrade_link;
      }
      return "";
    },
  },
  props: {
    instance: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card {
  height: 100%;
  min-height: 364px;
  &.card-plan {
    &-expires {
      background-color: var(--v-card-expires-bg);
    }
    &-expired {
      background-color: var(--v-card-expired-bg);
    }
  }
}

.details {
  display: flex;
  flex-direction: column;
  &:not(:first-child) {
    margin-top: 10px;
  }
  &-label {
  }
  &-value {
    font-weight: $font-weight-bold;
    margin-top: 4px;
    font-size: $font-size-base;
    line-height: $line-height-base;
  }
}

.v-skeleton-loader::v-deep {
  &:not(:last-child) {
    margin-bottom: 10px;
  }
  .v-skeleton-loader__text {
    &:first-child {
      height: 8px;
      width: 62px;
      margin: 6px 0 10px 0;
    }
    &:nth-child(2) {
      height: 10px;
      width: 148px;
      margin: 7px 0;
    }
  }
}
</style>